import { cn, formatDate, TimeFormats } from "@theprelude/core";
import { forwardRef } from "react";
import { DayPicker } from "react-day-picker";
import pickerStyles from "react-day-picker/dist/style.module.css";
import { CalendarIcon } from "../icons/calendar-icon";
import { CloseIcon } from "../icons/close-icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  usePopover,
} from "../popover/popover";
import { Input } from "./input";

const classNames: typeof pickerStyles = {
  ...pickerStyles,
  months: `${pickerStyles.months} m-2`,
  button_next: `${pickerStyles.button_next} bg-transparent`,
  button_previous: `${pickerStyles.button_previous} bg-transparent`,
  chevron: `${pickerStyles.chevron} fill-primary-foreground`,
  selected: `${pickerStyles.selected} rounded-full font-bold`,
  month_caption: `${pickerStyles.month_caption} mx-3`,
  weekday: `${pickerStyles.weekday} text-secondary-foreground font-bold opacity-100`,
  day_button: `${pickerStyles.day_button} rounded-full hover:border-4 hover:border-tertiary-background hover:border-full`,
  day: `${pickerStyles.day} text-xs text-primary-foreground`,
  today: "text-primary-foreground font-bold",
};

export interface DateInputProps {
  onBlur: React.ComponentProps<"input">["onBlur"];
  onChange: (s: string | null) => void;
  id?: string;
  name: string;
  value: string | null;
  disabled?: boolean;
  required?: boolean;
  wrapperClassName?: string;
  readOnly?: boolean;
  placeholder?: string;
  "data-testid"?: string;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  function DateInput(props, ref) {
    const popover = usePopover();
    return (
      <Popover {...popover}>
        <PopoverTrigger
          disabled={props.disabled || props.readOnly}
          className={cn("w-max bg-transparent p-0", props.wrapperClassName)}
        >
          <Input
            ref={ref}
            id={props.id}
            name={props.name}
            after={
              <div className="flex items-center gap-2">
                {props.value && (
                  <CloseIcon
                    tabIndex={0}
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onChange(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        props.onChange(null);
                      }
                    }}
                  />
                )}
                <CalendarIcon />
              </div>
            }
            value={props.value ?? ""}
            onBlur={props.onBlur}
            onChange={(e) => {
              const value = e.target.value;
              // Allow typing and validate date format
              if (
                (value === "" || /^\d{4}-\d{2}-\d{2}$/.test(value)) &&
                (value === "" || !isNaN(Date.parse(value)))
              ) {
                props.onChange(value || null);
              }
            }}
            placeholder={props.placeholder ?? "yyyy-mm-dd"}
            required={props.required}
            readOnly={props.readOnly}
            data-testid={props["data-testid"]}
          />
        </PopoverTrigger>
        <PopoverContent>
          <DayPicker
            mode="single"
            defaultMonth={
              props.value
                ? new Date(props.value.replaceAll("-", "/"))
                : undefined
            }
            selected={
              props.value
                ? new Date(props.value.replaceAll("-", "/"))
                : undefined
            }
            modifiersClassNames={{
              selected: "bg-tertiary-background text-white rounded-full",
            }}
            classNames={classNames}
            onSelect={(d) => {
              if (d) {
                props.onChange(formatDate(d, TimeFormats.DATE_SHORT));
              }
              popover.onOpenChange(false);
            }}
          />
        </PopoverContent>
      </Popover>
    );
  },
);
